import { Injectable } from '@angular/core';
import { Platform } from '@ionic/angular';
import { environment } from 'src/environments/environment';
import { AuthTokenService } from 'src/app/shared/services/auth-token.service';
import { AccountService } from 'src/app/shared/services/account.service';
import { StandardHttpProvider } from '../shared/services/standard-http.provider';
import { ApiInterfaceService } from './services/api-interface.service';
import { BaseService } from './services/base.service';

/** Type definition for request parameters */
// type ReqParams = string | URLSearchParams | { [key: string]: any | any[] };
type ReqParams = { [param: string]: string };

/**
 * Base utilities to interact with remote backend services.
 */
@Injectable({
  providedIn: 'root',
})
export class Remote {
  /** Class members */
  protected isLocalHost: boolean;

  protected api: string;

  protected apiHeaders: object;

  private isCordova: boolean = this.platform.is('cordova');

  private apiKey: string = environment.apiKey;

  constructor(
    private accountService: AccountService,
    protected platform: Platform,
    private standardHttp: StandardHttpProvider,
    public baseAPI: ApiInterfaceService,
    private authTokenService: AuthTokenService,
  ) {
    this.api = environment.apiUrl();
    this.apiHeaders = environment.apiHeaders;
    this.isLocalHost = environment.isLocalHost;
  }

  apiUrl(): string {
    return this.api;
  }

  // Retrieve remote resource by ID (shared by ServicesProvider & RemoteStore)
  // IMPORTANT: currently only available for conversations and jobs.
  read<T>(service: BaseService, id: number, params?: any): Promise<T> {
    const url = `${this.api}/${this.endpointFor(service)}/${id}`;

    return this.apiCallRetry('GET', url, params).then((res) => res.body.data.attributes) as Promise<T>;
  }

  // Map service to backend API endpoint
  protected endpointFor(service: BaseService): string {
    const prefix = service.acctScoped ? `${this.acctPrefix()}/` : '';
    const apiVersion = service.apiVersion ? `${service.apiVersion}/` : '';

    return apiVersion + prefix + service.resourceRepresentation();
  }

  // Perform a call to the API. NOTE: Previously this would check if auth token is expired
  // if it is renew token first before making api call - but this is now handled by the token refresh interceptor.
  protected apiCallRetry(
    method: string,
    url: string,
    params?: ReqParams,
    body?: any,
    headers?: any,
    serializer?: any,
  ): Promise<any> {
    return this.apiCall(method, url, params, body, headers, serializer);
  }

  /** Private utility members */

  private acctPrefix(): string {
    const clients = (account) => (account === 'Consumer' ? 'client' : account);
    const workers = (account) => (account === 'Carer' ? 'worker' : account);
    const prefix = workers(clients(this.accountService.getUser()?.custom['account_type'] || 'Carer'));

    return `${prefix.toLowerCase()}s`;
  }

  // Perform a single call to the API
  private apiCall(
    method: string,
    url: string,
    params?: ReqParams,
    body?: any,
    headers?: any,
    serializer?: any,
  ): Promise<any> {
    const alteredUrl = this.adjust_json_ext(url);

    if (method === 'GET' && params) {
      params = this.formatData(params);
    }

    // if we have a serializer passed in, we need to add something to the request to let the native http interceptor (native-http.interceptor.ts)
    // know to set the serializer on this api call. We will do so via a header. Note that this header will be removed before the api call is sent.
    let serializerSettingHeader = {};
    if (this.isCordova && serializer) {
      serializerSettingHeader = { 'Set-Serializer': serializer };
    }

    /// Because default Content-Type header is application/json we are overriding it for accepting form data.
    let requestHeaders = this.headers();
    if (headers) {
      requestHeaders = { ...this.headers(), ...headers, ...serializerSettingHeader };
      if (!requestHeaders['Content-Type']) {
        delete requestHeaders['Content-Type'];
      }
    }

    // we'll set it back after the call is complete
    return this.standardHttp.request(method, alteredUrl, requestHeaders, params, body);
  }

  // Add .json extension to access localhost in DEV environment
  private adjust_json_ext(url: string): string {
    if (this.isLocalHost) {
      return url.includes('?') ? url.replace('?', '.json?') : `${url}.json`;
    }
    return url;
  }

  private headers() {
    return {
      Authorization: `Bearer ${this.authTokenService.accessToken}`,
      'X-API-KEY': this.apiKey,
      'Content-Type': 'application/json',
      isWeb: 'false',
      ...this.apiHeaders,
    };
  }

  // Native http param bug with boolean, number, and object values
  // need to convert them to strings
  // https://github.com/angular/angular/issues/23856
  private formatData(data): any {
    const params = {};

    if (typeof data !== 'object') {
      return;
    }

    Object.keys(data).forEach((key) => {
      let value = data[key];
      const valueType = typeof value;

      if (value === null || value === undefined || value === '') {
        return;
      }

      if (valueType === 'boolean' || valueType === 'number' || valueType === 'object') {
        value = JSON.stringify(value);
      }

      params[key] = value;
    });

    return params;
  }
}
